import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Search, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { errMsg } from "@/lib/api";
import { DddPicker, useDddCounts } from "./DddPicker";

const ORIGINS = [["", "Qualquer origem"], ["instagram", "Instagram"], ["youtube", "YouTube"], ["site", "Site da igreja"], ["campanha", "Campanhas"], ["outros", "Outros canais"]];
const EMPTY = { name: "", everyone: false, state: "", city: "", category: "", origin: "", member_ids: [], ddds: [] };
const sel = "h-10 w-full rounded-md border border-zap-line bg-white px-2 text-sm";

const Manual = ({ api, ids, setIds, names, setNames }) => {
  const [q, setQ] = useState("");
  const [found, setFound] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setFound([]); return undefined; }
    const t = setTimeout(() => api.get("/admin/participants", { params: { q } }).then((r) => setFound(r.data.slice(0, 8))).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [api, q]);
  const add = (p) => { if (!ids.includes(p.id)) { setIds([...ids, p.id]); setNames({ ...names, [p.id]: p.name }); } setQ(""); };
  return (
    <div className="space-y-2">
      <label className="relative block">
        <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-muted-foreground" />
        <Input data-testid="group-member-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar pessoa por nome ou WhatsApp" className="pl-8" />
      </label>
      {found.length > 0 && (
        <ul className="max-h-40 overflow-y-auto rounded-md border border-zap-line">
          {found.map((p) => <li key={p.id}><button type="button" data-testid={`group-add-${p.id}`} onClick={() => add(p)} className="w-full px-3 py-2 text-left text-sm hover:bg-zap-mist">{p.name} <span className="text-muted-foreground">· {p.city}/{p.state}</span></button></li>)}
        </ul>
      )}
      {ids.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {ids.map((id) => <span key={id} className="flex items-center gap-1 rounded-full bg-zap-mist py-1 pl-3 pr-1 text-sm">{names[id] || "Participante"}
            <button type="button" aria-label="Tirar" onClick={() => setIds(ids.filter((x) => x !== id))} className="rounded-full p-1 hover:bg-zap-line"><X className="h-3.5 w-3.5" /></button></span>)}
        </div>
      )}
    </div>
  );
};

export const GroupDialog = ({ open, onClose, group, onSaved }) => {
  const { api } = useOutletContext();
  const [f, setF] = useState(EMPTY);
  const [names, setNames] = useState({});
  const [cats, setCats] = useState([]);
  const [preview, setPreview] = useState(null);
  const dddRows = useDddCounts(open);
  useEffect(() => {
    if (!open) return;
    setF(group ? { ...EMPTY, ...group } : EMPTY);
    setNames({});
    if (group) api.get(`/admin/groups/${group.id}/members`).then((r) => setNames(Object.fromEntries(r.data.map((p) => [p.id, p.name])))).catch(() => {});
    api.get("/admin/categories").then((r) => setCats(r.data.filter((c) => c.slug && c.slug !== "aguardando"))).catch(() => {});
  }, [open, group, api]);
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => api.post("/admin/groups/preview", { ...f, name: f.name.trim().length >= 2 ? f.name : "Grupo" }).then((r) => setPreview(r.data)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [open, f, api]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    try {
      const { data } = group ? await api.put(`/admin/groups/${group.id}`, f) : await api.post("/admin/groups", f);
      toast.success(group ? "Grupo atualizado." : "Grupo criado.");
      onSaved(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="group-dialog" className="max-h-[90dvh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{group ? "Editar grupo" : "Novo grupo"}</DialogTitle>
          <DialogDescription>Escolha quem recebe. Os participantes não veem uns aos outros.</DialogDescription>
        </DialogHeader>
        <label className="space-y-1 text-sm font-medium">Nome do grupo
          <Input data-testid="group-name" value={f.name} onChange={set("name")} placeholder="Ex.: Pedidos de oração de outubro" />
        </label>
        <button type="button" data-testid="group-select-all" onClick={() => setF({ ...f, everyone: !f.everyone })} aria-pressed={f.everyone}
          className={`flex h-12 items-center justify-center gap-2 rounded-md border-2 text-base font-bold transition-colors ${f.everyone ? "border-zap-deep bg-zap-deep text-white" : "border-zap-deep text-zap-deep hover:bg-zap-mist"}`}>
          <input type="checkbox" data-testid="group-everyone" checked={f.everyone} readOnly tabIndex={-1} className="pointer-events-none h-5 w-5 accent-[#0E4D3A]" />
          {f.everyone ? "Todos selecionados" : "Selecionar todos"}
        </button>
        <div className="space-y-1.5">
          <p className="text-sm font-medium">{f.everyone ? "Só dos DDDs (opcional)" : "Por DDD"}</p>
          <DddPicker rows={dddRows} value={f.ddds || []} onChange={(ddds) => setF({ ...f, ddds })} testPrefix="group-ddd" />
        </div>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">{f.everyone ? "Só quem também for de…" : "Ou filtrar por"}</legend>
          <div className="grid grid-cols-[80px_1fr] gap-2">
            <Input data-testid="group-state" value={f.state} maxLength={2} onChange={(e) => setF({ ...f, state: e.target.value.toUpperCase() })} placeholder="UF" />
            <Input data-testid="group-city" value={f.city} onChange={set("city")} placeholder="Cidade" />
          </div>
          <select data-testid="group-category" value={f.category} onChange={set("category")} className={sel}>
            <option value="">Qualquer assunto</option>
            {cats.map((c) => <option key={c.slug} value={c.slug}>Já enviou: {c.name}</option>)}
          </select>
          <select data-testid="group-origin" value={f.origin} onChange={set("origin")} className={sel}>
            {ORIGINS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </fieldset>
        <div className="space-y-1">
          <p className="text-sm font-medium">Incluir pessoas específicas</p>
          <Manual api={api} ids={f.member_ids} setIds={(ids) => setF({ ...f, member_ids: ids })} names={names} setNames={setNames} />
        </div>
        <p data-testid="group-preview-count" className="rounded-md border border-zap-line p-3 text-sm">
          <strong className="text-lg text-zap-deep">{preview ? preview.count : "…"}</strong> {preview?.count === 1 ? "pessoa vai receber" : "pessoas vão receber"}
          {preview?.members?.length > 0 && <span className="mt-1 block truncate text-muted-foreground">{preview.members.slice(0, 6).map((p) => p.name.split(" ")[0]).join(", ")}{preview.count > 6 ? "…" : ""}</span>}
        </p>
        <Button data-testid="group-save" onClick={save} disabled={f.name.trim().length < 2} className="h-11">{group ? "Salvar grupo" : "Criar grupo"}</Button>
      </DialogContent>
    </Dialog>
  );
};
